// storageApi.ts

interface UploadResponse {
  path: string;
  publicUrl: string;
  filename: string;
}

interface ListResponse {
  files: Array<{
    name: string;
    path: string;
    publicUrl: string;
    createdAt: string;
    size: number;
  }>;
}

export async function uploadPublicImage(file: File): Promise<UploadResponse> {
  try {
    // Converti il file in base64
    const base64 = await fileToBase64(file);

    const payload = {
      filename: file.name,
      content: base64,
      contentType: file.type || 'image/jpeg'
    };

    // Upload con timeout
    const response = await fetchWithTimeout('/api/upload-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    }, 60000); // 60 secondi timeout

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Errore sconosciuto' }));
      throw new Error(errorData.error || `Errore HTTP: ${response.status}`);
    }

    return await response.json();
    
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Errore durante l\'upload del file');
  }
}

export async function listPublicImages(limit: number = 200, offset: number = 0): Promise<Array<{
  name: string;
  publicUrl: string;
  createdAt?: string;
}>> {
  try {
    const response = await fetch(`/api/list-images?limit=${limit}&offset=${offset}`);
    
    if (!response.ok) {
      throw new Error(`Errore HTTP: ${response.status}`);
    }

    const data: ListResponse = await response.json();
    return data.files || [];
    
  } catch (error) {
    console.error('Errore nel recupero delle immagini:', error);
    throw error;
  }
}

// Funzioni helper
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Errore nella lettura del file'));
    reader.readAsDataURL(file);
  });
}

async function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error?.name === 'AbortError') {
      throw new Error('Timeout - la richiesta ha impiegato troppo tempo');
    }
    throw error;
  }
}